import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}

function pickArray(res: any) {
  const d = pick(res);

  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(res?.data)) return res.data;

  return [];
}

export type MemberSearchResult = {
  id: string;          // UUID from searchm, or user_id integer (as string) from old search
  member_id?: string;  // explicit member UUID — present on old /search endpoint
  user_id?: number;
  membership_no?: string;
  surname?: string;
  firstname?: string;
  othernames?: string | null;
  email?: string;
  phone?: string;
};
export type Committee = {
  id: number;
  name: string;
  slug?: string | null;
  description?: string | null;

  executive_set_id?: number | null;
  executive_set?: {
    id: number;
    name: string;
    start_date?: string;
    end_date?: string;
  };

  role_id?: number | null;
  role?: { id: number; name: string };

  is_active?: boolean;

  members?: any[];
  members_count?: number;

  created_at?: string;
  updated_at?: string;
};

export type CreateCommitteePayload = {
  name: string;
  description?: string;
  role_name?: string; // optional
  is_active?: boolean;
};

export type UpdateCommitteePayload = {
  id: number;
  name?: string;
  description?: string | null;
  role_name?: string | null; // allow clear
  is_active?: boolean;
};

export type AddCommitteeMemberPayload = {
  committeeId: number;
  body: {
    member_id: string; // uuid
    committee_role?: "chairman" | "secretary" | "member";
    position?: string;
  };
};

export type RemoveCommitteeMemberPayload = {
  committeeId: number;
  memberId: string; // uuid
};

export const committeesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // LIST
    getCommittees: builder.query<Committee[], void>({
      query: () => "/committees",
      transformResponse: (res: any) => pickArray(res),
      providesTags: (result) => {
        const list = Array.isArray(result) ? result : [];
        return list.length
          ? [
              ...list.map((c) => ({ type: "Committees" as const, id: c.id })),
              { type: "Committees" as const, id: "LIST" },
            ]
          : [{ type: "Committees" as const, id: "LIST" }];
      },
    }),

    // DETAILS
    getCommitteeDetails: builder.query<Committee, number>({
      query: (id) => `/committees/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "Committees" as const, id }],
    }),

    // CREATE
    createCommittee: builder.mutation<any, CreateCommitteePayload>({
      query: (body) => ({
        url: "/committees",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Committees" as const, id: "LIST" }],
    }),

    // UPDATE
    updateCommittee: builder.mutation<any, UpdateCommitteePayload>({
      query: ({ id, ...body }) => ({
        url: `/committees/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Committees" as const, id: "LIST" },
        { type: "Committees" as const, id: arg.id },
      ],
    }),

    // DELETE
    deleteCommittee: builder.mutation<any, number>({
      query: (id) => ({
        url: `/committees/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Committees" as const, id: "LIST" }],
    }),

    // ADD MEMBER
    addCommitteeMember: builder.mutation<any, AddCommitteeMemberPayload>({
      query: ({ committeeId, body }) => ({
        url: `/committees/${committeeId}/members`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Committees" as const, id: arg.committeeId },
        { type: "Committees" as const, id: "LIST" },
      ],
    }),

    // REMOVE MEMBER
    removeCommitteeMember: builder.mutation<any, RemoveCommitteeMemberPayload>({
      query: ({ committeeId, memberId }) => ({
        url: `/committee-member/${committeeId}/members/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Committees" as const, id: arg.committeeId },
        { type: "Committees" as const, id: "LIST" },
      ],
    }),

    searchMembers: builder.query<MemberSearchResult[], string>({
      query: (q) => `/members/searchm/${encodeURIComponent(q)}`,
      transformResponse: (res: any) => {
        const d = pick(res);
        return Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];
      },
    }),

    getActiveApplicationCommitteeMembers: builder.query<any, void>({
      query: () => `/committees/application/active/members`,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCommitteesQuery,
  useGetCommitteeDetailsQuery,
  useCreateCommitteeMutation,
  useUpdateCommitteeMutation,
  useDeleteCommitteeMutation,
  useAddCommitteeMemberMutation,
  useRemoveCommitteeMemberMutation,
  useSearchMembersQuery,
  useGetActiveApplicationCommitteeMembersQuery,
} = committeesApi;
